import React, {
  useContext,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useState,
} from "react";
import { useForm } from "@mantine/form";
import { TextInput, Select, Textarea } from "@mantine/core";
import { UserContext } from "@/contexts/UserContext/UserContext";
import { notifications } from "@mantine/notifications";
import { Validatable } from "@/pages/study/StudyStepper";
import { useLocation } from "react-router-dom";
import { getFormStyles } from "@/helpers/getFormStyles";
import { ProjectDataType } from "@/types/apiTypes";
import styles from "../../css/ship.module.css";
import { useAppColors } from "@/hooks/contextHooks/useAppColors";

type Props = {
  projects: ProjectDataType[];
};

const ShipFormStep1 = forwardRef<Validatable, Props>(({ projects }, ref) => {
  const context = useContext(UserContext);
  const location = useLocation();
  const projectId = location.state?.projectId;
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    projectId || null,
  );

  const { isDarkMode } = useAppColors();
  const formStyles = getFormStyles(isDarkMode);

  const form = useForm({
    initialValues: {
      shipName: context?.formValues.shipName || "",
      loadCaseName: context?.formValues.loadCaseName || "",
      comment: context?.formValues.comment || "",
      // additionalInfo: context?.formValues.additionalInfo || '',
      projectId: selectedProjectId || "",
    },
    validate: {
      shipName: (value: string) =>
        value.trim() ? null : "Ship name is required",
      loadCaseName: (value: string) =>
        value.trim() ? null : "Load case name is required",
      comment: (value: string) => (value.trim() ? null : "Comment is required"),
      // additionalInfo: (value: string) => (value ? null : 'Additional info is required'),
    },
  });

  useEffect(() => {
    if (selectedProjectId) {
      context?.setFormValues((prev) => ({
        ...prev,
        projectId: selectedProjectId,
      }));
    }
  }, [context, selectedProjectId]);

  const handleSubmit = (values: typeof form.values) => {
    context?.setFormValues((prev) => ({ ...prev, ...values }));
  };

  const validate = () => {
    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      Object.values(validationResult.errors).forEach((error) => {
        if (error) {
          notifications.show({
            title: "Validation Error",
            message: error,
            color: "red",
          });
        }
      });
      return false;
    }
    context?.setFormValues((prev) => ({
      ...prev,
      ...form.values,
      projectId: selectedProjectId ?? "",
    }));
    return true;
  };

  useImperativeHandle(ref, () => ({
    validate,
  }));

  return (
    <div className={styles.stepContainer}>
      <form id="ship-form-step-0" onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          label="Ship name"
          {...form.getInputProps("shipName")}
          required
          styles={formStyles}
        />
        <TextInput
          label="Load case name"
          {...form.getInputProps("loadCaseName")}
          required
          styles={formStyles}
        />
        <Textarea
          label="Comment"
          {...form.getInputProps("comment")}
          required
          styles={formStyles}
        />
        {/* <TextInput label="Additional Info" {...form.getInputProps('additionalInfo')} required /> */}

        <Select
          label="Project"
          placeholder="Select a project"
          data={projects.map((project) => ({
            value: project.id,
            label: project.name,
          }))}
          value={selectedProjectId}
          onChange={(value) => setSelectedProjectId(value)}
          styles={formStyles}
          // required
        />
      </form>
    </div>
  );
});

export default ShipFormStep1;
